/* eslint-disable react-hooks/exhaustive-deps */
import {Col, Row, Tag} from "antd"
import {BaseButton} from "components"
import {handleError, toastSuccess} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useVisible} from "hooks"
import {isEmpty} from "lodash"
import React, {useCallback, useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {KlassappTableProps} from "types/common"
import {FilterKey} from "types/filter"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import PaymentMethodPopup from "./PaymentMethodPopup"
import RetailersGatewayPaymentMethodPopup from "./RetailersGatewayPaymentMethodPopup"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import styles from "./PaymentMethods.module.css"
import {PaymentProvider, StudentPaymentMethod, StudentPaymentMethodType} from "types/student-account/student-payment"

const INITIAL_FILTER = {}

// Retailers Gateway stores bank accounts in the bank columns; show them instead of the empty card fields
const isRetailersGatewayAch = (record: StudentPaymentMethod) =>
    record.provider === PaymentProvider.RetailersGateway && record.type === StudentPaymentMethodType.Ach

type Props = KlassappTableProps & {
    profileId: number
}

const PaymentMethods: React.FC<Props> = (props: Props) => {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        isLoading,
        menuActions,
        isHideMenuActions,
        isShowTableHeaderAction,
        isLoadedTableFuncs,
        onClickShowConfirmModal,
        getCurrentData,
        getCurrentPage,
        getCurrentPageSize,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onDraggableColumn,
        onChangeAllFields,
        onUpdateRowData,
        onUpdateTableData,
        profileId
    } = props
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const filterKey = FilterKey.StudentAccountPaymentMethods
    const storageData = model.getStorageFilter(filterKey)
    const {t} = useTranslation(["payment", "common"])
    const {
        paymentProviderConfigs: [primaryProviderConfig]
    } = usePaymentProviderConfigs(profileId)
    // Same gateway the checkout charges with (its first config), so added methods are usable there
    const addWithRetailersGateway = primaryProviderConfig?.provider === PaymentProvider.RetailersGateway

    //#region filter

    const [search, setSearch] = useState(!isEmpty(storageData) ? storageData.search : "")
    const [filter, setFilter] = useState(!isEmpty(storageData?.filter) ? storageData.filter : INITIAL_FILTER)
    const [appliedFilter, setAppliedFilter] = useState(filter)

    const onChangeFilter = useCallback((key: keyof typeof filter, value: any) => {
        setFilter((prev) => ({...prev, [key]: value}))
    }, [])

    const onClearFilter = useCallback(() => {
        setFilter(INITIAL_FILTER)
        setAppliedFilter(INITIAL_FILTER)
    }, [])

    const onApplyFilter = useCallback(() => {
        setAppliedFilter(filter)
        model.updateStorageFilter(filterKey, {filter})
    }, [filterKey, filter])

    const onSearchInput = useCallback(
        (search = "") => {
            setSearch(search)
            model.updateStorageFilter(filterKey, {search})
        },
        [filterKey]
    )

    //#endregion filter

    const getFilters = useCallback(() => {
        return {
            search
        }
    }, [profileId, search, appliedFilter])

    const getData = React.useCallback(async () => {
        try {
            dispatch({isLoading: true})
            const page = getCurrentPage()
            const pageSize = getCurrentPageSize()
            const filter = getFilters()
            const data = await studentPaymentService.getStudentPaymentMethods(profileId)
            dispatch({total: data.length, data: data.map((item) => ({...item, id: item.paymentMethodId}))})
        } catch (error) {
            console.error(error)
        } finally {
            dispatch({isLoading: false})
        }
    }, [profileId, getFilters])

    const onClickRowItem = React.useCallback((item) => {}, [])

    const onClickEdit = React.useCallback((item) => {}, [])

    const onClickDelete = React.useCallback(
        async (item: StudentPaymentMethod) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.deleteStudentPaymentMethod(item.paymentMethodId)
                await getData()
            } catch (e) {
                handleError(e)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData, profileId]
    )

    const onClickSetActive = React.useCallback(
        async (item: StudentPaymentMethod) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.setActiveStudentPaymentMethod(item.paymentMethodId)
                toastSuccess(t("msg.paymentMethod.setActiveSuccessfully"))
                await getData()
            } catch (e) {
                handleError(e)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData, t]
    )

    const onClickDeleteMulti = React.useCallback(async () => {
        const checkedItems: StudentPaymentMethod[] = getCurrentData().filter((item) => item.isChecked)
        try {
            dispatch({isLoading: true, isShowTableHeaderAction: false, isHideMenuActions: false})
            await Promise.all(
                checkedItems.map((item) => studentPaymentService.deleteStudentPaymentMethod(item.paymentMethodId))
            )
            await getData()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }, [getData, profileId])

    const getFields = React.useCallback(
        () =>
            [
                t("column.id"),
                t("column.brand"),
                t("column.last4"),
                t("column.cardName"),
                t("column.expiration"),
                t("column.active")
            ].filter(Boolean),
        [t]
    )

    const getColumns = React.useCallback(
        () => [
            {
                title: t("column.id"),
                field: "paymentMethodId",
                columnIndex: 1
            },
            {
                title: t("column.brand"),
                field: "cardBrand",
                columnIndex: 2,
                render: (value, record: StudentPaymentMethod) =>
                    isRetailersGatewayAch(record) ? record.bankName || "ACH" : value
            },
            {
                title: t("column.last4"),
                field: "last4",
                columnIndex: 4,
                render: (value, record: StudentPaymentMethod) =>
                    `**** ${isRetailersGatewayAch(record) ? record.bankAccountNumber : value}`
            },
            {
                title: t("column.cardName"),
                field: "cardHolderName",
                columnIndex: 3,
                render: (value, record: StudentPaymentMethod) =>
                    isRetailersGatewayAch(record) ? record.bankAccountName : value
            },
            {
                title: t("column.expiration"),
                field: "expMonth",
                columnIndex: 5,
                render: (value, record: StudentPaymentMethod) =>
                    isRetailersGatewayAch(record) ? "-" : `${record.expMonth}/${record.expYear}`
            },
            {
                title: t("column.active"),
                field: "isActive",
                columnIndex: 6,
                render: (value) => (value ? <Tag color="green">{t("label.active")}</Tag> : null)
            }
        ],
        [t]
    )

    const getTableHeaderActions = React.useCallback(
        (isShowDuplicateBtn = true, checkedData = []) => {
            const actions = [
                {
                    title: t("common:action.delete"),
                    icon: "DELETE",
                    action: () => onClickShowConfirmModal("DELETE")
                }
            ]
            return actions
        },
        [t, onClickShowConfirmModal]
    )

    const getMenuActions = React.useCallback(() => {
        return [
            {
                title: t("label.setActive"),
                icon: "CHECK_CIRCLE",
                action: onClickSetActive,
                canShow: (item: StudentPaymentMethod) => !item.isActive
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete,
                canShow: (item) => true
            }
        ]
    }, [t, onClickDelete, onClickSetActive])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: () => t("label.paymentMethods")},
            {key: "getFilterMemoryKey", func: () => filterKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "onClickRowItem", func: onClickRowItem},
            {key: "onClickSetActive", func: onClickSetActive}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, search, appliedFilter, profileId])

    const paymentMethodPopup = useVisible(false)

    const handleAddedPaymentMethodSuccess = useCallback(() => {
        paymentMethodPopup.close()
        getData()
    }, [getData])

    return (
        <div className={styles.wrap}>
            <BaseNewFilter
                className="mb-24"
                filter={filter}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}
                renderRightFilter={() => (
                    <div className="flex justify-end items-center">
                        <BaseButton
                            title={t("common:action.add")}
                            className="ml-24"
                            onClick={paymentMethodPopup.open}
                        />
                    </div>
                )}
                onSearchInput={onSearchInput}>
                <Row gutter={[24, 24]}>
                    <Col span={12}></Col>
                </Row>
            </BaseNewFilter>

            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                menuActions={isHideMenuActions ? [] : menuActions}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn
                onClickRowItem={onClickRowItem}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />

            {addWithRetailersGateway ? (
                <RetailersGatewayPaymentMethodPopup
                    isShow={paymentMethodPopup.isVisible}
                    onClose={paymentMethodPopup.close}
                    onSuccess={handleAddedPaymentMethodSuccess}
                    profileId={profileId}
                />
            ) : (
                <PaymentMethodPopup
                    isShow={paymentMethodPopup.isVisible}
                    onClose={paymentMethodPopup.close}
                    onSuccess={handleAddedPaymentMethodSuccess}
                    profileId={profileId}
                />
            )}
        </div>
    )
}

export default KlassappTableHOC(PaymentMethods)
